import { buildQuery, shopFetch, shopFetchRaw, throwFromResponse } from './shopClient';
import { resolveProductResponse } from '../utils/productRedirect';
import type {
  ProductBySlugResult,
  ShopCategoriesResponse,
  ShopCategory,
  ShopOrderConfirmation,
  ShopCheckoutPayload,
  ShopProductsResponse,
  ShopSettings,
  ShopSortOption,
} from '../types';

export interface GetShopProductsParams {
  q?: string;
  categoryId?: string;
  inStock?: true;
  minPrice?: number;
  maxPrice?: number;
  sort?: ShopSortOption;
  limit?: number;
  cursor?: string | null;
}

export const getShopSettings = (signal?: AbortSignal): Promise<ShopSettings> =>
  shopFetch<ShopSettings>('/settings', { signal });

export const getShopCategories = async (signal?: AbortSignal): Promise<ShopCategory[]> => {
  const data = await shopFetch<ShopCategoriesResponse>('/categories', { signal });
  return Array.isArray(data?.items) ? data.items : [];
};

export const getShopProducts = (
  params: GetShopProductsParams,
  signal?: AbortSignal
): Promise<ShopProductsResponse> =>
  shopFetch<ShopProductsResponse>(`/products${buildQuery({ ...params })}`, { signal });

/**
 * Resolves a product by slug. Unlike the other endpoints this one inspects the
 * raw response, because a 301 is a meaningful success case rather than an error.
 */
export const getShopProductBySlug = async (
  slug: string,
  signal?: AbortSignal
): Promise<ProductBySlugResult> => {
  const { res, body } = await shopFetchRaw(`/products/${encodeURIComponent(slug)}`, { signal });

  // 301/308 carry the redirect payload; anything else non-2xx is a real error.
  if (!res.ok && res.status !== 301 && res.status !== 308) {
    throwFromResponse(res, body);
  }

  return resolveProductResponse(slug, res, body);
};

export const postShopCheckout = (
  payload: ShopCheckoutPayload,
  signal?: AbortSignal
): Promise<ShopOrderConfirmation> =>
  shopFetch<ShopOrderConfirmation>('/checkout', { method: 'POST', body: payload, signal });
